/**
 * Dearlove — Build Your Bundle (landing pub "pick any 5").
 *
 * Grille filtrable de produits éligibles au bundle. Le clic sur une carte
 * ouvre un modal type quick-view (galerie + variantes + personnalisation)
 * adapté à nos produits : chaque pièce ajoutée porte ses propres line item
 * properties (gravure, birthstones...). Ajout via /cart/add.js, re-render du
 * cart drawer via le contrat Dawn. Aucune app, tout natif.
 *
 * La remise réelle (ex. -15% dès 3 pièces) est appliquée au checkout par les
 * automatic discounts admin sur la collection bundle-eligible. Le tray affiche
 * juste la progression vers l'objectif (target).
 */

if (!customElements.get('dl-bundle-builder')) {
  customElements.define(
    'dl-bundle-builder',
    class DlBundleBuilder extends HTMLElement {
      connectedCallback() {
        this.target = parseInt(this.dataset.target, 10) || 5;
        this.count = parseInt(this.dataset.initCount, 10) || 0;

        this.overlay = this.querySelector('.dl-bb-overlay');
        this.modalBody = this.querySelector('.dl-bb-modal__body');
        this.tray = this.querySelector('.dl-bb-tray');
        this.toast = this.querySelector('.dl-bb-toast');
        this.cart =
          document.querySelector('cart-drawer') || document.querySelector('cart-notification');

        this.bindFilters();
        this.bindCards();
        this.bindModal();
        this.bindGuide();
        this.bindTray();
        this.initProducts();
        this.bindReview();
        this.renderTray();

        // Le compteur se relit toujours sur le vrai panier : retrait dans le tiroir,
        // dans un autre onglet, ou retour arrière (page restaurée avec l'ancien compte).
        // PUB_SUB_EVENTS est un const global de constants.js, pas une propriété de window.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartSub = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (!event || event.source !== 'dl-bundle-builder') this.syncCount();
          });
        }
        this.onShow = () => {
          if (document.visibilityState === 'visible') this.syncCount();
        };
        window.addEventListener('pageshow', this.onShow);
        document.addEventListener('visibilitychange', this.onShow);
      }

      disconnectedCallback() {
        if (this.cartSub) this.cartSub();
        window.removeEventListener('pageshow', this.onShow);
        document.removeEventListener('visibilitychange', this.onShow);
      }

      // Redessine le tiroir panier du site sans l'ouvrir (renderContents l'ouvrirait).
      refreshDrawer(data) {
        if (!this.cart || !data || !data.sections) return;
        this.cart.getSectionsToRender().forEach((section) => {
          const el = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
          const html = data.sections[section.id];
          if (el && html) el.innerHTML = this.cart.getSectionInnerHTML(html, section.selector);
        });
        this.cart.classList.toggle('is-empty', !this.cart.querySelector('.cart-item'));
      }

      syncCount() {
        return fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((cart) => {
            this.count = this.bundleCount(cart.items);
            this.renderTray();
            return cart;
          })
          .catch((e) => console.error('[dl-bundle-builder]', e));
      }

      /* ---- Filtres ---- */
      bindFilters() {
        this.filters = Array.from(this.querySelectorAll('.dl-bb-filter'));
        this.cards = Array.from(this.querySelectorAll('.dl-bb-card'));
        this.filters.forEach((btn) => {
          btn.addEventListener('click', () => {
            this.filters.forEach((b) => b.classList.toggle('is-active', b === btn));
            const type = btn.dataset.filter;
            this.cards.forEach((card) => {
              card.hidden = type !== 'all' && card.dataset.type !== type;
            });
          });
        });
      }

      /* ---- Cartes -> ouverture modal ---- */
      bindCards() {
        this.querySelectorAll('.dl-bb-card').forEach((card) => {
          card.addEventListener('click', () => this.openProduct(card.dataset.productId));
          // Survol = intention d'ouvrir : la galerie est en cache avant le clic.
          card.addEventListener('pointerenter', () => this.warmProduct(card.dataset.productId, 1), {
            passive: true,
          });
        });
      }

      warmProduct(id, n) {
        const block = this.querySelector(`.dl-bb-product[data-product-id="${id}"]`);
        if (block && block._gallery) block._gallery.warm(n);
      }

      bindGuide() {
        const dialog = this.querySelector('#dl-bb-size-guide');
        if (!dialog) return;
        // Délégation : le lien est rendu dans les 33 configurateurs.
        this.addEventListener('click', (e) => {
          if (e.target.closest('[data-bb-guide-open]') && !dialog.open) dialog.showModal();
        });
        dialog.addEventListener('click', (e) => {
          if (e.target === dialog || e.target.closest('[data-bb-guide-close]')) dialog.close();
        });
      }

      bindModal() {
        if (!this.overlay) return;
        this.overlay.addEventListener('click', (e) => {
          if (e.target === this.overlay || e.target.closest('.dl-bb-close')) this.closeModal();
        });
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.overlay.classList.contains('is-open')) this.closeModal();
        });
      }

      openProduct(id) {
        const block = this.querySelector(`.dl-bb-product[data-product-id="${id}"]`);
        if (!block) return;
        this.querySelectorAll('.dl-bb-product').forEach((b) => b.classList.toggle('is-active', b === block));
        this.overlay.classList.add('is-open');
        document.body.classList.add('dl-bb-locked');
        this.modalBody.scrollTop = 0;
        if (block._gallery) block._gallery.warm();
        this.resolveVariant(block);
        this.applyStoneCount(block);
      }

      closeModal() {
        this.overlay.classList.remove('is-open');
        document.body.classList.remove('dl-bb-locked');
      }

      /* ---- Init par produit : galerie, variantes, stones ---- */
      initProducts() {
        this.querySelectorAll('.dl-bb-product').forEach((block) => {
          this.bindGallery(block);
          this.bindOptions(block);
          this.bindAdd(block);
        });
      }

      bindGallery(block) {
        const main = block.querySelector('.dl-bb-gallery__main img');
        const strip = block.querySelector('.dl-bb-gallery__thumbs');
        const thumbs = Array.from(block.querySelectorAll('.dl-bb-gallery__thumb'));
        if (!main || !thumbs.length) return;
        let index = 0;
        let token = 0;

        // La vignette active est amenée dans la bande par scrollLeft, JAMAIS par
        // scrollIntoView : celui-ci fait défiler TOUS les ancêtres, donc le corps
        // de la modale et la page. Un changement de variante ne doit rien bouger
        // d'autre que la galerie.
        const revealThumb = (t) => {
          if (!strip) return;
          const left = t.offsetLeft - strip.clientWidth / 2 + t.offsetWidth / 2;
          strip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
        };

        // L'image affichée n'est remplacée qu'une fois la nouvelle décodée.
        // Assigner src directement vide l'élément : le visiteur voyait un cadre
        // blanc pendant 0,5 à 1,3 s, le temps du téléchargement (mesuré).
        const show = (i) => {
          index = (i + thumbs.length) % thumbs.length;
          const t = thumbs[index];
          thumbs.forEach((th, j) => th.classList.toggle('is-active', j === index));
          revealThumb(t);
          const url = t.dataset.full || t.querySelector('img').src;
          if (main.getAttribute('src') === url) return;
          const mine = ++token;
          const next = new Image();
          next.src = url;
          const apply = () => {
            if (mine === token) main.src = url;
          };
          if (next.decode) next.decode().then(apply, apply);
          else if (next.complete) apply();
          else {
            next.onload = apply;
            next.onerror = apply;
          }
        };

        const showByMediaId = (id) => {
          const i = thumbs.findIndex((t) => t.dataset.mediaId === String(id));
          if (i >= 0 && i !== index) show(i);
        };

        // Les images de la galerie sont en lazy dans un bloc caché : rien n'est
        // chargé avant l'ouverture. On les met en cache dès qu'on sait que le
        // visiteur va les voir, sinon chaque clic paie son téléchargement.
        // Bornée : au survol on ne précharge QUE la première image, sinon
        // balayer la grille de 33 cartes lancerait des centaines de requêtes.
        let warmedTo = 0;
        const warm = (n) => {
          const upTo = Math.min(n || thumbs.length, thumbs.length);
          for (; warmedTo < upTo; warmedTo += 1) {
            const url = thumbs[warmedTo].dataset.full;
            if (url) new Image().src = url;
          }
        };

        thumbs.forEach((t, i) => t.addEventListener('click', () => show(i)));
        const prev = block.querySelector('.dl-bb-gallery__nav--prev');
        const nextBtn = block.querySelector('.dl-bb-gallery__nav--next');
        if (prev) prev.addEventListener('click', () => show(index - 1));
        if (nextBtn) nextBtn.addEventListener('click', () => show(index + 1));
        block._gallery = { show, showByMediaId, warm };
      }

      bindOptions(block) {
        // Swatch pills
        block.querySelectorAll('.dl-bb-swatches').forEach((group) => {
          group.querySelectorAll('.dl-bb-swatch').forEach((btn) => {
            btn.addEventListener('click', () => {
              group.querySelectorAll('.dl-bb-swatch').forEach((b) => b.classList.toggle('is-selected', b === btn));
              const opt = group.closest('.dl-bb-opt');
              const valEl = opt && opt.querySelector('.dl-bb-opt__value');
              if (valEl) valEl.textContent = btn.dataset.value;
              this.resolveVariant(block);
            });
          });
        });
        // Selects (Size, Number of Stones, Message card...)
        block.querySelectorAll('.dl-bb-opt__select').forEach((sel) => {
          sel.addEventListener('change', () => {
            this.resolveVariant(block);
            if (sel.hasAttribute('data-bb-stones')) this.applyStoneCount(block);
          });
        });
      }

      readSelectedOptions(block) {
        const opts = Array.from(block.querySelectorAll('.dl-bb-opt'))
          .sort((a, b) => parseInt(a.dataset.optPos, 10) - parseInt(b.dataset.optPos, 10))
          .map((opt) => {
            const swatch = opt.querySelector('.dl-bb-swatch.is-selected');
            if (swatch) return swatch.dataset.value;
            const sel = opt.querySelector('.dl-bb-opt__select');
            if (sel) return sel.value;
            return null;
          });
        return opts;
      }

      resolveVariant(block) {
        const data = this.variantData(block);
        if (!data) return;
        const selected = this.readSelectedOptions(block);
        let match = data.find((v) => v.opts.length === selected.length && v.opts.every((o, i) => o === selected[i]));
        if (!match) match = data.find((v) => v.available) || data[0];
        block.dataset.variantId = match.id;
        this.renderPrice(block, match);
        this.applyConditionalFields(block);
        // La galerie suit la variante, comme sur la fiche produit. Aucun défilement
        // n'est déclenché : show() ne touche qu'à la bande de vignettes.
        if (match.media && block._gallery) block._gallery.showByMediaId(match.media);
        const addBtn = block.querySelector('.dl-bb-add');
        if (addBtn) {
          addBtn.disabled = !match.available;
          const label = addBtn.querySelector('.dl-bb-add__label');
          if (label && !match.available) label.textContent = 'Sold out';
          else if (label) label.textContent = addBtn.dataset.label || 'Add to bundle';
        }
      }

      variantData(block) {
        if (block._variants) return block._variants;
        const script = block.querySelector('[data-bb-variants]');
        if (!script) return null;
        try {
          block._variants = JSON.parse(script.textContent);
        } catch (e) {
          block._variants = null;
        }
        return block._variants;
      }

      renderPrice(block, v) {
        const box = block.querySelector('.dl-bb-price');
        if (!box) return;
        let html = `<span class="dl-bb-price__current">${v.price}</span>`;
        if (v.compare) html += `<span class="dl-bb-price__compare">${v.compare}</span>`;
        if (v.save > 0) html += `<span class="dl-bb-price__chip">Save ${v.save}%</span>`;
        box.innerHTML = html;
      }

      /* ---- Number of Stones : N champs birthstone visibles + requis ---- */
      applyStoneCount(block) {
        const sel = block.querySelector('[data-bb-stones]');
        if (!sel) return;
        const count = parseInt(sel.value, 10);
        if (!count) return;
        block.querySelectorAll('[data-dl-field]').forEach((wrap) => {
          const idx = parseInt(wrap.dataset.fieldIndex, 10);
          if (!idx) return;
          const show = idx <= count;
          wrap.hidden = !show;
          wrap.dataset.required = show ? 'true' : 'false';
          const input = wrap.querySelector('select, input');
          if (input) {
            if (show) input.setAttribute('required', '');
            else input.removeAttribute('required');
          }
        });
      }

      /* ---- Champs « when » (gravure payante) : visibles si l'option a la valeur attendue ---- */
      applyConditionalFields(block) {
        block.querySelectorAll('[data-dl-field][data-when-option]').forEach((wrap) => {
          const opt = Array.from(block.querySelectorAll('.dl-bb-opt')).find((o) => o.dataset.optName === wrap.dataset.whenOption);
          let value = null;
          if (opt) {
            const swatch = opt.querySelector('.dl-bb-swatch.is-selected');
            const sel = opt.querySelector('.dl-bb-opt__select');
            value = swatch ? swatch.dataset.value : sel ? sel.value : null;
          }
          wrap.hidden = value !== wrap.dataset.whenValue;
        });
      }

      /* ---- Validation + collecte des properties ---- */
      validate(block) {
        let firstInvalid = null;
        block.querySelectorAll('[data-dl-field]').forEach((wrap) => {
          if (wrap.hidden) return;
          const required = wrap.dataset.required !== 'false';
          const input = wrap.querySelector('input, select');
          const error = wrap.querySelector('.dl-perso__error');
          // « min » (data-min) : nombre exact de caractères attendus, espaces non comptés
          const min = input ? parseInt(input.dataset.min || '0', 10) : 0;
          const invalid = required && input && (!input.value.trim() || (min > 0 && input.value.replace(/\s/g, '').length < min));
          wrap.classList.toggle('has-error', invalid);
          if (error) error.hidden = !invalid;
          if (invalid && !firstInvalid) firstInvalid = input;
        });
        if (firstInvalid) {
          const stone = firstInvalid.closest('dl-stone-select');
          const focusTarget = stone ? stone.querySelector('.dl-stone-select__toggle') : firstInvalid;
          if (focusTarget) focusTarget.focus();
          return false;
        }
        return true;
      }

      collectProperties(block) {
        const props = {};
        block.querySelectorAll('[data-dl-field]').forEach((wrap) => {
          if (wrap.hidden) return;
          const input = wrap.querySelector('input, select');
          if (input && input.value.trim()) props[wrap.dataset.label] = input.value;
        });
        return props;
      }

      /* ---- Add to bundle ---- */
      bindAdd(block) {
        const btn = block.querySelector('.dl-bb-add');
        if (!btn) return;
        btn.addEventListener('click', () => this.addToBundle(block, btn));
      }

      addToBundle(block, btn) {
        if (!this.validate(block)) return;
        const variantId = parseInt(block.dataset.variantId, 10);
        if (!variantId) return;

        const properties = this.collectProperties(block);
        // Marqueur pour la Shopify Function : seules les pièces ajoutées ICI
        // (page bundle) comptent pour le prix "5 for $99". Préfixe "_" = masqué
        // dans le drawer Dawn. Les mêmes produits ajoutés ailleurs n'ont pas ce
        // marqueur → pas de remise bundle.
        properties['_bundle'] = 'pick5';
        const item = { id: variantId, quantity: 1, properties };
        const body = { items: [item] };
        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
        }

        btn.classList.add('loading');
        btn.setAttribute('aria-disabled', 'true');

        fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        })
          .then((r) => r.json())
          .then((response) => {
            if (response.status) {
              console.error('[dl-bundle-builder]', response.description || response.message);
              return;
            }
            const wasBelowTarget = this.count < this.target;
            this.count += 1;
            this.lastCartResponse = response;
            this.renderTray();
            this.flashCard(block.dataset.productId);
            // On garde le contrat cartUpdate (barre free-shipping, badge header...)
            // mais on N'OUVRE PAS le drawer ici : cart.renderContents() l'ouvre
            // (cart-drawer.js), or on ne veut l'ouvrir qu'à la complétion du bundle.
            if (this.cart && response.sections && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'dl-bundle-builder',
                productVariantId: variantId,
                cartData: response,
              });
            }
            this.refreshDrawer(response);
            this.showToast();
            this.closeModal();
            // Le panier ne s'ouvre QUE quand le bundle est complet (passage
            // sous le seuil -> au-dessus). Au-delà, on ne le ré-ouvre pas.
            // Recalage sur le vrai panier, puis récap à la complétion du lot.
            this.syncCount().then(() => {
              if (wasBelowTarget && this.count >= this.target) this.openReview();
            });
          })
          .catch((e) => console.error('[dl-bundle-builder]', e))
          .finally(() => {
            btn.classList.remove('loading');
            btn.removeAttribute('aria-disabled');
          });
      }

      flashCard(id) {
        const card = this.querySelector(`.dl-bb-card[data-product-id="${id}"]`);
        if (!card) return;
        const badge = card.querySelector('.dl-bb-card__cta');
        if (badge) {
          const original = badge.dataset.original || badge.textContent;
          badge.dataset.original = original;
          badge.textContent = 'Added ✓';
          setTimeout(() => (badge.textContent = original), 1600);
        }
      }

      /* ---- Tray ---- */
      bindTray() {
        if (!this.tray) return;
        const cta = this.tray.querySelector('.dl-bb-tray__cta');
        if (cta) cta.addEventListener('click', () => this.goToCart());
      }

      goToCart() {
        this.openReview();
      }

      /* ---- Récap du lot ---- */
      // Seules les lignes ajoutées depuis cette page (marqueur _bundle) comptent.
      bundleCount(items) {
        return items
          .filter((it) => it.properties && it.properties._bundle)
          .reduce((n, it) => n + it.quantity, 0);
      }

      bindReview() {
        this.review = this.querySelector('.dl-bb-review');
        if (!this.review) return;
        this.review.addEventListener('click', (e) => {
          if (e.target === this.review || e.target.closest('.dl-bb-review__close')) this.closeReview();
          const remove = e.target.closest('[data-bb-remove]');
          if (remove) this.removeLine(remove);
          if (e.target.closest('[data-bb-pick]')) this.keepPicking();
        });
        this.review.querySelector('.dl-bb-review__primary').addEventListener('click', () => {
          if (this.count >= this.target) window.location.href = '/checkout';
          else this.keepPicking();
        });
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.review.classList.contains('is-open')) this.closeReview();
        });
      }

      openReview() {
        if (!this.review) return this.openCart(this.lastCartResponse);
        this.review.classList.add('is-open');
        document.body.classList.add('dl-bb-locked');
        this.loadReview();
      }

      closeReview() {
        this.review.classList.remove('is-open');
        document.body.classList.remove('dl-bb-locked');
      }

      keepPicking() {
        this.closeReview();
        const grid = this.querySelector('#dl-bb-grid');
        if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      loadReview() {
        return fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((cart) => this.renderReview(cart))
          .catch((e) => console.error('[dl-bundle-builder]', e));
      }

      money(cents, currency) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency',
            currency,
            minimumFractionDigits: cents % 100 ? 2 : 0,
          }).format(cents / 100);
        } catch (e) {
          return (cents / 100).toFixed(2);
        }
      }

      renderReview(cart) {
        const lines = cart.items.filter((it) => it.properties && it.properties._bundle);
        this.count = this.bundleCount(cart.items);
        this.renderTray();

        const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
        const list = this.review.querySelector('.dl-bb-review__list');
        let html = '';
        lines.forEach((it) => {
          // Récap lisible : nom + variante seulement, la perso reste visible au checkout.
          const meta = it.variant_title ? esc(it.variant_title) : '';
          const img = it.image ? `${it.image}${it.image.includes('?') ? '&' : '?'}width=160` : '';
          for (let q = 0; q < it.quantity; q++) {
            html +=
              `<li class="dl-bb-review__item">` +
              (img ? `<img src="${img}" alt="" width="64" height="64" loading="lazy">` : '<span></span>') +
              `<div class="dl-bb-review__info"><p class="dl-bb-review__name">${esc(it.product_title.split(' — ')[0])}</p>` +
              (meta ? `<p class="dl-bb-review__meta">${meta}</p>` : '') +
              `</div><button type="button" class="dl-bb-review__remove" data-bb-remove data-key="${esc(it.key)}" data-qty="${it.quantity}" aria-label="Remove ${esc(it.product_title)}">&times;</button></li>`;
          }
        });
        for (let i = lines.reduce((n, it) => n + it.quantity, 0); i < this.target; i++) {
          html += `<li class="dl-bb-review__item dl-bb-review__item--empty"><button type="button" data-bb-pick><span aria-hidden="true">+</span> Pick piece ${i + 1}</button></li>`;
        }
        list.innerHTML = html;

        const remaining = Math.max(0, this.target - this.count);
        const price = this.review.closest('dl-bundle-builder').dataset.bundlePrice;
        // final_line_price inclut déjà la remise du lot : le barré se calcule hors remise,
        // sur le prix barré de la variante (data-compare, posé par la section) quand il y en a un.
        const regular = lines.reduce((n, it) => n + it.final_line_price, 0);
        let compare = {};
        try {
          compare = JSON.parse(this.review.closest('dl-bundle-builder').dataset.compare || '{}');
        } catch (e) {}
        const beforeBundle = lines.reduce(
          (n, it) => n + Math.max(it.original_line_price, (compare[it.variant_id] || 0) * it.quantity),
          0
        );
        const priceCents = Math.round(parseFloat(String(price).replace(/[^0-9.,]/g, '').replace(',', '.')) * 100);
        this.review.querySelector('.dl-bb-review__count').textContent = `${this.count}/${this.target}`;
        const total = this.review.querySelector('.dl-bb-review__total');
        const primary = this.review.querySelector('.dl-bb-review__primary');
        const secondary = this.review.querySelector('.dl-bb-review__secondary');
        if (remaining === 0) {
          total.innerHTML = `<span>Bundle price</span><span>${beforeBundle > priceCents ? `<s>${this.money(beforeBundle, cart.currency)}</s> ` : ''}<strong>${esc(price)}</strong></span>`;
          primary.textContent = 'Checkout';
          secondary.hidden = true;
        } else {
          const piece = remaining === 1 ? 'piece' : 'pieces';
          total.innerHTML = `<span>Add ${remaining} more ${piece} to get all ${this.target} for <strong>${esc(price)}</strong></span>`;
          primary.textContent = `Pick ${remaining} more ${piece}`;
          secondary.hidden = this.count === 0;
          secondary.textContent = `Checkout now at ${this.money(regular, cart.currency)}`;
        }
      }

      // Retire UNE pièce du lot, puis remet le panier du site à jour sans l'ouvrir.
      removeLine(btn) {
        btn.disabled = true;
        const body = { id: btn.dataset.key, quantity: parseInt(btn.dataset.qty, 10) - 1 };
        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
        }
        fetch(`${window.routes.cart_change_url}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify(body),
        })
          .then((r) => r.json())
          .then((cart) => {
            this.refreshDrawer(cart);
            this.renderReview(cart);
          })
          .catch((e) => {
            btn.disabled = false;
            console.error('[dl-bundle-builder]', e);
          });
      }

      // Ouvre le cart drawer en le rafraîchissant avec la dernière réponse
      // /cart/add.js (cart.renderContents ouvre le drawer). Sinon fallback.
      openCart(response) {
        const data = response || this.lastCartResponse;
        if (this.cart && data && data.sections && typeof this.cart.renderContents === 'function') {
          this.cart.renderContents(data);
        } else if (this.cart && typeof this.cart.open === 'function') {
          this.cart.open();
        } else {
          window.location = window.routes.cart_url;
        }
      }

      renderTray() {
        if (!this.tray) return;
        this.tray.classList.toggle('is-visible', this.count > 0);
        const dots = this.tray.querySelectorAll('.dl-bb-tray__dot');
        dots.forEach((d, i) => d.classList.toggle('is-filled', i < this.count));
        const label = this.tray.querySelector('.dl-bb-tray__label');
        if (label) {
          const remaining = Math.max(0, this.target - this.count);
          if (remaining > 0) {
            const piece = remaining === 1 ? 'piece' : 'pieces';
            label.innerHTML =
              `<strong>${this.count}/${this.target}</strong>` +
              `<span class="dl-bb-tray__more"> selected · add ${remaining} more ${piece} to complete your bundle</span>` +
              `<span class="dl-bb-tray__short"> · ${remaining} more to add</span>`;
          } else {
            label.innerHTML =
              `<strong>${this.count} pieces</strong>` +
              `<span class="dl-bb-tray__more"> · your bundle discount is unlocked 🎉</span>` +
              `<span class="dl-bb-tray__short"> · unlocked 🎉</span>`;
          }
        }
      }

      showToast() {
        if (!this.toast) return;
        this.toast.classList.add('is-visible');
        clearTimeout(this._toastT);
        this._toastT = setTimeout(() => this.toast.classList.remove('is-visible'), 2200);
      }
    }
  );
}
